import { Link } from 'react-router-dom';

import { JsonLd } from '@/components/JsonLd';
import { PageMeta } from '@/components/PageMeta';
import { CompetitionCard, CompetitionSkeleton } from '@/components/home/competition-card';
import FeaturesGrid from '@/components/home/features-grid';
import ProblemSolution from '@/components/home/problem-solution';
import { FaqSection, TestimonialsSection } from '@/components/home/testimonials-faq';
import { ROUTES } from '@/config/routes';
import { getCompetitionsApi } from '@/lib/api/competitionsApi';
import Demo from '@/pages/demo.tsx';
import { CompetitionStatus } from '@shared/enums/domain.enum';
import type { Competition } from '@shared/types/entities';
import { useQuery } from '@tanstack/react-query';
import { ArrowRight, Clock, CreditCard, PlayCircle, ShieldCheck, Trophy, User, Users2 } from 'lucide-react';

export default function Home() {
  const { data: openComps, isLoading: compsLoading } = useQuery({
    queryKey: ['home', 'open-competitions'],
    queryFn: () =>
      getCompetitionsApi({
        status: CompetitionStatus.REGISTRATION_OPEN,
        upcoming: true,
        limit: 6,
        sortBy: 'registrationEnd',
        sortOrder: 'asc',
      }),
    staleTime: 5 * 60 * 1000,
  });

  const competitions: Competition[] = openComps?.data ?? [];

  return (
    <>
      <PageMeta
        title='BoulesConnect'
        description='La plateforme SaaS de gestion de concours de pétanque, jeu provençal et boule lyonnaise. Inscriptions en ligne, paiements sécurisés, saisie des numéros de licence et exports compatibles Gestion Concours FFPJP. Commission Partenaire 2 % + 0,30 € (min 0,75 €), 0 € fixe.'
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'BoulesConnect',
          url: 'https://boulesconnect.fr',
          description: 'La plateforme SaaS de gestion de concours de pétanque, jeu provençal et boule lyonnaise.',
          potentialAction: {
            '@type': 'SearchAction',
            target: {
              '@type': 'EntryPoint',
              urlTemplate: 'https://boulesconnect.fr/concours?search={search_term_string}',
            },
            'query-input': 'required name=search_term_string',
          },
        }}
      />
      <JsonLd
        data={{
          '@context': 'https://schema.org',
          '@type': 'Organization',
          name: 'BoulesConnect',
          url: 'https://boulesconnect.fr',
          logo: 'https://boulesconnect.fr/apple-touch-icon.png',
          contactPoint: {
            '@type': 'ContactPoint',
            contactType: 'customer support',
            availableLanguage: 'French',
            url: 'https://boulesconnect.fr/contact',
          },
        }}
      />

      {/* ── Hero ─────────────────────────────────────────────────────────── */}
      <section className='pt-40 pb-20 px-6'>
        <div className='max-w-5xl mx-auto text-center'>
          <div className='animate-framer inline-flex items-center gap-2 dark:bg-slate-900 border border-blue-100 dark:border-blue-900 px-4 py-2 rounded-full mb-8'>
            <span className='relative flex h-2 w-2'>
              <span className='animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75'></span>
              <span className='relative inline-flex rounded-full h-2 w-2 bg-blue-500'></span>
            </span>
            <span className='text-xs font-bold text-blue-700 dark:text-blue-300 uppercase tracking-wider'>
              Pétanque · Jeu provençal · Boule lyonnaise
            </span>
          </div>

          <h1 className='animate-framer delay-1 text-5xl md:text-7xl font-extrabold text-slate-900 dark:text-white leading-[1.1] mb-8'>
            Organisez vos concours
            <br />
            <span className='gradient-text'>sans paperasse ni stress.</span>
          </h1>

          <p className='animate-framer delay-2 text-lg text-slate-500 dark:text-slate-400 max-w-2xl mx-auto mb-6 leading-relaxed'>
            BoulesConnect centralise la gestion de vos compétitions de pétanque, jeu provençal et boule lyonnaise :
            inscriptions en ligne, paiements sécurisés, suivi des équipes, podium public et exports pour les
            organisateurs.
          </p>

          <div className='animate-framer delay-3 flex flex-wrap items-center justify-center gap-8 mb-6 text-sm'>
            <div className='flex items-center gap-2'>
              <Trophy className='w-5 h-5 text-green-500' />
              <span className='text-slate-600 dark:text-slate-300'>
                <strong className='text-slate-900 dark:text-white'>Concours</strong> : inscriptions et résultats en
                ligne
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <CreditCard className='w-5 h-5 text-blue-500' />
              <span className='text-slate-600 dark:text-slate-300'>
                <strong className='text-slate-900 dark:text-white'>Paiements</strong> : encaissement sécurisé via Stripe
              </span>
            </div>
            <div className='flex items-center gap-2'>
              <ShieldCheck className='w-5 h-5 text-emerald-500' />
              <span className='text-slate-600 dark:text-slate-300'>
                <strong className='text-slate-900 dark:text-white'>Licences</strong> : numéro renseigné par le joueur
              </span>
            </div>
          </div>

          <p className='text-xs text-slate-500 dark:text-slate-400 max-w-2xl mx-auto leading-relaxed mb-10'>
            BoulesConnect n’est pas affilié à la FFPJP et ne représente pas la FFPJP. Le numéro de licence est renseigné
            par le joueur ; BoulesConnect ne vérifie pas automatiquement sa validité.
          </p>

          <div className='animate-framer delay-4 flex flex-col sm:flex-row items-center justify-center gap-4'>
            <Link
              to={ROUTES.REGISTER}
              className='w-full sm:w-auto bg-gradient-to-br from-blue-600 to-indigo-600 text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-2xl hover:opacity-90 transition-all flex items-center justify-center gap-2 hover-lift'
            >
              Essayer gratuitement <ArrowRight className='w-5 h-5' />
            </Link>
            <Link
              to={ROUTES.SEARCH_CONCOURS}
              className='w-full sm:w-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-white px-10 py-4 rounded-2xl font-bold text-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-all flex items-center justify-center gap-2 hover-lift'
            >
              <Trophy className='w-5 h-5' /> Voir les concours
            </Link>
          </div>

          <p className='animate-framer delay-5 mt-5 text-slate-400 text-sm'>
            Commission Partenaire 2 % + 0,30 € (min 0,75 €) · 0 € fixe · Sans engagement
          </p>
        </div>
      </section>

      <hr />

      {/* Démo interactive — embedded : pas de PageMeta (sinon écrase le title accueil), h1 rétrogradé en h2 */}
      <Demo embedded />

      {/* ── Concours ouverts ─────────────────────────────────────────────── */}
      <section className='px-6 pb-24'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex flex-col sm:flex-row sm:items-end justify-between gap-4 mb-10'>
            <div>
              <span className='text-blue-600 font-bold text-sm uppercase tracking-wider'>En ce moment</span>
              <h2 className='text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mt-2'>
                Concours ouverts aux inscriptions
              </h2>
              <p className='text-slate-500 dark:text-slate-400 mt-2'>
                Rejoignez une équipe ou inscrivez la vôtre avant qu'il ne soit trop tard.
              </p>
            </div>
            <Link
              to={ROUTES.SEARCH_CONCOURS}
              className='flex-shrink-0 inline-flex items-center gap-2 text-blue-600 dark:text-blue-400 font-bold hover:gap-3 transition-all'
            >
              Voir tous les concours <ArrowRight className='w-4 h-4' />
            </Link>
          </div>

          {compsLoading ? (
            <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              <CompetitionSkeleton />
              <CompetitionSkeleton />
              <CompetitionSkeleton />
            </div>
          ) : competitions.length === 0 ? (
            <div className='flex flex-col items-center justify-center py-20 text-center bg-slate-50 dark:bg-slate-900/50 rounded-3xl border border-dashed border-slate-200 dark:border-slate-700'>
              <Trophy className='w-12 h-12 text-slate-300 dark:text-slate-600 mb-4' />
              <p className='text-slate-500 dark:text-slate-400 font-medium'>
                Aucun concours ouvert aux inscriptions pour le moment.
              </p>
              <Link
                to={ROUTES.SEARCH_CONCOURS}
                className='mt-4 text-blue-600 dark:text-blue-400 font-bold flex items-center gap-1 hover:gap-2 transition-all'
              >
                Consulter tous les concours <ArrowRight className='w-4 h-4' />
              </Link>
            </div>
          ) : (
            <div className='grid sm:grid-cols-2 lg:grid-cols-3 gap-6'>
              {competitions.slice(0, 6).map(comp => (
                <CompetitionCard key={comp.id} comp={comp} />
              ))}
            </div>
          )}

          {competitions.length > 0 && (
            <div className='flex justify-center mt-10'>
              <Link
                to={ROUTES.SEARCH_CONCOURS}
                className='inline-flex items-center gap-2 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-white font-bold px-8 py-3 rounded-2xl transition-colors'
              >
                Voir tous les concours <ArrowRight className='w-4 h-4' />
              </Link>
            </div>
          )}
        </div>
      </section>

      {/* ── Dashboard preview ────────────────────────────────────────────── */}
      <section className='px-6 pb-24'>
        <div className='max-w-6xl mx-auto relative'>
          <div className='text-center mb-12'>
            <span className='text-blue-600 font-bold text-sm uppercase tracking-wider'>Pour les organisateurs</span>
            <h2 className='text-3xl md:text-4xl font-extrabold text-slate-900 dark:text-white mt-2'>
              Un backoffice taillé pour les pros
            </h2>
            <p className='text-slate-500 dark:text-slate-400 mt-2 max-w-xl mx-auto'>
              Gérez vos concours, inscriptions, paiements et résultats depuis un tableau de bord centralisé.
            </p>
          </div>

          <div className='animate-scale delay-5 relative'>
            <div className='absolute -top-20 -left-20 w-64 h-64 bg-blue-400/20 rounded-full blur-[100px]'></div>
            <div className='absolute -bottom-20 -right-20 w-64 h-64 bg-indigo-400/20 rounded-full blur-[100px]'></div>
            <div className='relative bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-[32px] shadow-2xl overflow-hidden hover-lift'>
              {/* Badge "Aperçu" ajouté */}
              <div className='absolute top-4 right-4 z-10 bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-[10px] font-black uppercase tracking-wider px-3 py-1 rounded-full border border-amber-200 dark:border-amber-700'>
                Aperçu · Données d'exemple
              </div>

              <div className='border-b border-slate-100 dark:border-slate-800 p-6 flex justify-between items-center bg-slate-50/50 dark:bg-slate-800/50'>
                <div className='flex gap-2'>
                  <div className='w-3 h-3 rounded-full bg-slate-200 dark:bg-slate-600'></div>
                  <div className='w-3 h-3 rounded-full bg-slate-200 dark:bg-slate-600'></div>
                  <div className='w-3 h-3 rounded-full bg-slate-200 dark:bg-slate-600'></div>
                </div>
                <div className='text-xs font-bold text-slate-400 tracking-widest uppercase'>
                  Espace Organisateur — Club Pétanque Lyon 3
                </div>
                <div className='w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900 text-blue-600 dark:text-blue-300 flex items-center justify-center'>
                  <User className='w-4 h-4' />
                </div>
              </div>
              {/* … le reste du dashboard preview inchangé … */}
              <div className='p-8'>
                <div className='grid grid-cols-1 md:grid-cols-3 gap-6 mb-10'>
                  <div className='bg-blue-600 rounded-2xl p-6 text-white shadow-xl shadow-blue-200 dark:shadow-blue-900'>
                    <p className='text-blue-100 text-sm font-medium mb-1'>Inscriptions actives</p>
                    <h4 className='text-3xl font-black'>
                      128 <span className='text-sm font-normal opacity-80'>/ 150</span>
                    </h4>
                  </div>
                  <div className='bg-slate-50 dark:bg-slate-800 rounded-2xl p-6 border border-slate-100 dark:border-slate-700'>
                    <p className='text-slate-500 dark:text-slate-400 text-sm font-medium mb-1'>Collecte Inscriptions</p>
                    <h4 className='text-3xl font-black text-slate-800 dark:text-white'>1 920 €</h4>
                  </div>
                  <div className='bg-emerald-50 dark:bg-emerald-900/20 rounded-2xl p-6 border border-emerald-100 dark:border-emerald-800'>
                    <p className='text-emerald-600 dark:text-emerald-400 text-sm font-medium mb-1'>
                      Temps gagné / mois
                    </p>
                    <h4 className='text-3xl font-black text-emerald-600 dark:text-emerald-400'>jusqu'à 3h</h4>
                  </div>
                </div>
                {/* table concours à venir — inchangée */}
                <div className='space-y-4'>
                  <h5 className='font-bold text-slate-800 dark:text-white'>Concours à venir</h5>
                  <div className='overflow-x-auto'>
                    <table className='w-full text-left'>
                      <thead>
                        <tr className='text-slate-400 text-xs uppercase font-bold border-b border-slate-50 dark:border-slate-800'>
                          <th className='pb-4'>Nom du concours</th>
                          <th className='pb-4'>Date</th>
                          <th className='pb-4'>Statut</th>
                          <th className='pb-4 text-right'>Action</th>
                        </tr>
                      </thead>
                      <tbody className='text-sm'>
                        <tr className='border-b border-slate-50 dark:border-slate-800'>
                          <td className='py-4 font-bold text-slate-800 dark:text-white'>Grand Prix Doublette Senior</td>
                          <td className='py-4 text-slate-500'>12 Mars 2026</td>
                          <td className='py-4'>
                            <span className='bg-emerald-100 dark:bg-emerald-900/40 text-emerald-600 dark:text-emerald-400 px-3 py-1 rounded-full text-xs font-bold'>
                              Inscriptions Ouvertes
                            </span>
                          </td>
                          <td className='py-4 text-right'>
                            <button className='text-blue-600 font-bold hover:underline text-xs uppercase tracking-wider'>
                              Détails
                            </button>
                          </td>
                        </tr>
                        <tr>
                          <td className='py-4 font-bold text-slate-800 dark:text-white'>Tête-à-tête Vétérans</td>
                          <td className='py-4 text-slate-500'>24 Mars 2026</td>
                          <td className='py-4'>
                            <span className='bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 px-3 py-1 rounded-full text-xs font-bold'>
                              Brouillon
                            </span>
                          </td>
                          <td className='py-4 text-right'>
                            <button className='text-blue-600 font-bold hover:underline text-xs uppercase tracking-wider'>
                              Éditer
                            </button>
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ProblemSolution />
      <FeaturesGrid />

      {/* ── Engagements strip ─────────────────────────────────────────────── */}
      <section className='px-6 py-20 bg-linear-to-br from-blue-600 to-indigo-600'>
        <div className='max-w-5xl mx-auto text-center text-white'>
          <p className='text-blue-200 font-bold text-xs uppercase tracking-[0.2em] mb-3'>
            Pensé pour les sports de boules
          </p>
          <h2 className='text-2xl md:text-3xl font-extrabold mb-10'>
            Une plateforme claire pour organiser, inscrire et suivre vos concours
          </h2>
          <div className='grid grid-cols-2 md:grid-cols-4 gap-8'>
            {[
              {
                icon: <Trophy className='w-8 h-8 mx-auto mb-3 text-blue-200' />,
                value: 'Concours',
                label: 'Création, inscriptions et podium',
              },
              {
                icon: <Users2 className='w-8 h-8 mx-auto mb-3 text-blue-200' />,
                value: 'Équipes',
                label: 'Suivi joueurs et compositions',
              },
              {
                icon: <ShieldCheck className='w-8 h-8 mx-auto mb-3 text-blue-200' />,
                value: 'Licences',
                label: 'Saisie déclarative par le joueur',
              },
              {
                icon: <CreditCard className='w-8 h-8 mx-auto mb-3 text-blue-200' />,
                value: 'Stripe',
                label: 'Paiements sécurisés',
              },
            ].map(stat => (
              <div key={stat.label}>
                {stat.icon}
                <p className='text-3xl font-extrabold'>{stat.value}</p>
                <p className='text-blue-100 text-sm mt-1'>{stat.label}</p>
              </div>
            ))}
          </div>
          <p className='text-xs text-blue-200 mt-8 max-w-2xl mx-auto'>
            Les données affichées sur les concours, clubs et organisateurs proviennent des contenus réellement publiés
            sur la plateforme.
          </p>
        </div>
      </section>

      {/* ── TESTIMONIALS ─ */}
      <TestimonialsSection />

      {/* ── CTA Organisateur ─────────────────────────────────────────────── */}
      <section className='px-6 py-20'>
        <div className='max-w-5xl mx-auto'>
          <div className='bg-slate-900 dark:bg-slate-800 rounded-[2.5rem] p-10 md:p-16 flex flex-col md:flex-row items-center gap-10 text-white'>
            <div className='flex-1'>
              <div className='flex items-center gap-2 mb-4'>
                <Clock className='w-5 h-5 text-blue-400' />
                <span className='text-blue-400 font-bold text-sm uppercase tracking-wider'>
                  Commission Partenaire 2 % + 0,30 € (min 0,75 €) · 0 € fixe
                </span>
              </div>
              <h2 className='text-3xl md:text-4xl font-extrabold mb-4 leading-tight'>
                Votre prochain concours
                <br />
                <span className='text-blue-400'>mérite mieux qu'Excel.</span>
              </h2>
              <p className='text-slate-400 max-w-md'>
                Créez votre espace organisateur en quelques minutes. Concours illimités. Aucune carte bancaire requise.
              </p>
            </div>
            <div className='flex flex-col gap-4 shrink-0'>
              <Link
                to={ROUTES.REGISTER}
                className='bg-blue-600 hover:bg-blue-500 text-white font-bold px-10 py-4 rounded-2xl transition-colors flex items-center gap-2 justify-center'
              >
                Démarrer gratuitement <ArrowRight className='w-5 h-5' />
              </Link>
              <a
                href='https://cal.com/marcantoinedutoit/demo'
                target='_blank'
                rel='noopener noreferrer'
                className='border border-slate-700 hover:border-slate-500 text-slate-300 font-bold px-10 py-4 rounded-2xl transition-colors flex items-center gap-2 justify-center'
              >
                <PlayCircle className='w-5 h-5' /> Demander une démo
              </a>
            </div>
          </div>
        </div>
      </section>

      <FaqSection />

      {/* ── CTA Final ───────────────────────────────────────────────────── */}
      <section className='px-6 py-32 bg-linear-to-br from-blue-600 to-indigo-600 text-white relative overflow-hidden'>
        <div className='absolute inset-0 opacity-10'>
          <div className='absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl'></div>
          <div className='absolute bottom-0 right-0 w-96 h-96 bg-white rounded-full blur-3xl'></div>
        </div>
        <div className='max-w-4xl mx-auto text-center relative z-10'>
          <h2 className='text-4xl md:text-6xl font-extrabold mb-6 leading-tight'>
            Prêt à simplifier
            <br />
            vos concours ?
          </h2>
          <p className='text-xl text-blue-100 mb-12 max-w-2xl mx-auto'>
            Rejoignez les organisateurs, clubs et joueurs qui gèrent leurs concours avec BoulesConnect. Commission
            Partenaire 2 % + 0,30 € (min 0,75 €) · 0 € fixe · Sans engagement.
          </p>
          <div className='flex flex-col sm:flex-row items-center justify-center gap-4'>
            <Link
              to={ROUTES.REGISTER}
              className='w-full sm:w-auto bg-white text-blue-600 px-12 py-5 rounded-2xl font-bold text-lg shadow-2xl hover:bg-slate-50 transition-all flex items-center justify-center gap-2 hover-lift'
            >
              Démarrer gratuitement <ArrowRight className='w-5 h-5' />
            </Link>
            <a
              href='https://cal.com/marcantoinedutoit/demo'
              target='_blank'
              rel='noopener noreferrer'
              className='w-full sm:w-auto bg-transparent border-2 border-white text-white px-12 py-5 rounded-2xl font-bold text-lg hover:bg-white/10 transition-all flex items-center justify-center gap-2'
            >
              Demander une démo
            </a>
          </div>
          <p className='mt-8 text-blue-100 text-sm'>
            Aucune carte bancaire requise — Annulable à tout moment — Support en français
          </p>
        </div>
      </section>
    </>
  );
}
